import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Zap, Car, CreditCard } from "lucide-react";
import { FONT_FAMILY } from "../common/sharedFunctions";
import PageSEO from '../components/SEO/PageSEO';

export default function AboutUs() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const navigate = useNavigate();

  const features = [
    { icon: Shield, title: t('pruduct_section_heading_1'), desc: t('product_section_1'), color: 'purple' },
    { icon: Zap, title: t('pruduct_section_heading_2'), desc: t('product_section_2'), color: 'pink' },
    { icon: Car, title: t('pruduct_section_heading_3'), desc: t('product_section_3'), color: 'blue' },
    { icon: CreditCard, title: t('pruduct_section_heading_4'), desc: t('product_section_4'), color: 'green' },
  ];

  const colorMap = {
    purple: { bg: 'bg-[#009689]/10', hover: 'group-hover:bg-[#009689]/20', icon: 'text-[#009689]' },
    pink: { bg: 'bg-[#319F44]/10', hover: 'group-hover:bg-[#319F44]/20', icon: 'text-[#319F44]' },
    blue: { bg: 'bg-blue-100', hover: 'group-hover:bg-blue-200', icon: 'text-blue-600' },
    green: { bg: 'bg-green-100', hover: 'group-hover:bg-green-200', icon: 'text-green-600' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#009689]/5 via-white to-[#319F44]/5" style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <PageSEO
        title="About Us"
        description="Learn about Gruwcer Delivery - a modern delivery platform committed to safe, reliable, and affordable delivery services."
        keywords="about Gruwcer Delivery, delivery company, delivery platform, transportation service, mobility platform"
        path="/about-us"
      />
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <img src="/gruwcer-logo.png" alt="Gruwcer Delivery" onClick={() => navigate('/')} className="h-8 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: FONT_FAMILY }}
          >
            {t('about_us')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: FONT_FAMILY }}
          >
            {t('about_us_content2')}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base text-gray-500 max-w-3xl mx-auto mt-4 leading-relaxed"
            style={{ fontFamily: FONT_FAMILY }}
          >
            {t('about_us_content1')}
          </motion.p>
        </div>
      </section>

      {/* Features */}
      <section className="pb-20 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature, i) => {
            const colors = colorMap[feature.color];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#009689]/30 transition-all group"
              >
                <div className={`w-12 h-12 mb-4 rounded-xl ${colors.bg} ${colors.hover} flex items-center justify-center transition-colors`}>
                  <feature.icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: FONT_FAMILY }}>
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: FONT_FAMILY }}>
                  {feature.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
